import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Box,
    useMediaQuery
} from '@mui/material';
import { styled, useTheme } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// --- VIDEO ROTATION SOURCES ---
const BACKGROUND_VIDEOS = [
    'https://dto86tiwj6qnu.cloudfront.net/film_screening.mp4',
    'https://dto86tiwj6qnu.cloudfront.net/yoga_on_the_lawn.mp4',
    'https://dto86tiwj6qnu.cloudfront.net/cycling_in_the_street.mp4',
    'https://www-media.marsreel.com/wp-content/uploads/2019/03/19235924/MARS-REEL_SPLASH_V04C.mp4',
];

// --- STYLED COMPONENTS ---
const Root = styled('div')(({ isMobile }) => ({
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isMobile ? '#141414' : 'transparent',
}));

const VideoBackground = styled('video')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: -1,
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1,
});

const StyledContainer = styled(Container)({
    textAlign: 'center',
    paddingTop: '2rem',
    paddingBottom: '2rem',
    zIndex: 2,
    marginTop: '4rem',
});

const StyledCard = styled(Card)({
    backgroundColor: 'rgba(51, 51, 51, 0.8)',
    color: '#fff',
    borderRadius: 8,
    padding: '1rem',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
});

const CardTitle = styled(Typography)({
    fontSize: '1.5rem',
    fontWeight: 'bold',
});

const FreeSessionText = styled('span')({
    color: '#ffffff',
    textDecoration: 'none',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    zIndex: 2,
});

const ActionButton = styled(Button)({
    backgroundColor: '#C63D57',
    color: '#fff',
    '&:hover': {
        backgroundColor: '#a83348',
    },
});

const StepIndicator = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
    zIndex: 2,
});

// --- FOOTER STYLES ---
const RevertedFooter = styled('footer')({
    width: '100%',
    color: '#b3b3b3',
    padding: '2rem 1rem',
    textAlign: 'center',
    zIndex: 2,
    marginTop: 'auto',
});

const RevertedFooterLink = styled('a')({
    color: '#ffffff',
    textDecoration: 'none',
    transition: 'color 0.2s',
    '&:hover': {
        color: '#b3b3b3',
    },
    fontSize: '0.9rem',
});

const ChoosePlan = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [currentVideo, setCurrentVideo] = useState(0);

    // Rotate background videos every 8 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentVideo((prev) => (prev + 1) % BACKGROUND_VIDEOS.length);
        }, 8000);
        return () => clearInterval(interval);
    }, []);

    const handlePlanSelection = (plan) => {
        navigate('/create-account', { state: { selectedPlan: plan } });
    };

    const footerLinks = [
        { text: 'FAQ', href: '/faq' },
        { text: 'Help Center', href: '/help-center' },
        { text: 'Terms of Use', href: '/terms-of-use' },
        { text: 'Privacy', href: '/privacy' },
        { text: 'Company Policy', href: '/company-policy' },
        { text: 'Engineering & Data Science', href: '/engineering' },
        { text: 'United for Business', href: 'https://business.joinunited.ai' },
        { text: 'Ambassador Program', href: 'https://affiliate.marsreel.com' },
        { text: 'Apply to Work Clinics', href: 'https://contractors.joinunited.ai' },
    ];

    return (
        <Root isMobile={isMobile}>
            {!isMobile && (
                <>
                    <VideoBackground autoPlay loop muted key={currentVideo}>
                        <source src={BACKGROUND_VIDEOS[currentVideo]} type="video/mp4" />
                    </VideoBackground>
                    <Overlay />
                </>
            )}

            <StyledContainer maxWidth="md">
                <StepIndicator>
                    <CheckCircleIcon
                        style={{ marginRight: '0.5rem', color: '#C63D57', fontSize: '2rem' }}
                    />
                    <Typography variant="h5" style={{ fontSize: '2rem' }}>
                        Step 1 of 3
                    </Typography>
                </StepIndicator>

                <Typography variant="h4" gutterBottom>
                    Choose Your Plan
                </Typography>

                <Grid
                    container
                    spacing={isMobile ? 2 : 4}
                    justifyContent="center"
                    style={{ marginTop: '2rem' }}
                >
                    <Grid item xs={12} sm={8} md={5}>
                        <StyledCard>
                            <CardContent>
                                <CardTitle variant="h6" gutterBottom>
                                    Pay As You Go
                                </CardTitle>
                                <FreeSessionText>First Session is Free</FreeSessionText>
                                <Typography variant="body2" sx={{ color: '#b3b3b3', mt: 2, fontSize: '0.85rem', lineHeight: 1.5 }}>
                                    No commitment required. Sessions start at $40 each. Perfect for trying out different experiences on your own schedule.
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <ActionButton
                                    fullWidth
                                    variant="contained"
                                    onClick={() => handlePlanSelection('Pay As You Go')}
                                >
                                    Choose Plan
                                </ActionButton>
                            </CardActions>
                        </StyledCard>
                    </Grid>

                    <Grid item xs={12} sm={8} md={5}>
                        <StyledCard>
                            <CardContent>
                                <CardTitle variant="h6" gutterBottom>
                                    Recurring
                                </CardTitle>
                                <FreeSessionText>First Session is Free</FreeSessionText>
                                <Typography variant="body2" sx={{ color: '#b3b3b3', mt: 2, fontSize: '0.85rem', lineHeight: 1.5 }}>
                                    Starting at $150/month. Get discounted credits to use on any experience — the more you attend, the more you save.
                                </Typography>
                            </CardContent>
                            <CardActions>
                                <ActionButton
                                    fullWidth
                                    variant="contained"
                                    onClick={() => handlePlanSelection('Recurring')}
                                >
                                    Choose Plan
                                </ActionButton>
                            </CardActions>
                        </StyledCard>
                    </Grid>
                </Grid>
            </StyledContainer>

            {/* --- FOOTER JSX --- */}
            <RevertedFooter>
                <Typography variant="body2" sx={{ color: '#b3b3b3', mb: 2 }}>
                    Questions? Email us: hello@joinunited.ai
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: { xs: 2, sm: 3 },
                    }}
                >
                    {footerLinks.map((link) => (
                        <RevertedFooterLink
                            key={link.text}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {link.text}
                        </RevertedFooterLink>
                    ))}
                </Box>
            </RevertedFooter>
        </Root>
    );
};

export default ChoosePlan;